// ============================================================================
//  PastHistoryDialog — every other attendance recorded for this patient
// ============================================================================
//
//  Opened from Entries ▸ Past History. Read-only: nothing here changes the claim.
//
//  ── WHY THIS STOPPED SAYING "NO CLINICAL HISTORY" ──────────────────────────
//
//  It used to read /visitsummary, which the consulting room's Medical History
//  uses. That endpoint reads `claim_information` and `patient_date` — the tables
//  filled when somebody is REGISTERED at this hospital. A member whose care
//  arrived as an XML batch was never registered here, so it returned nothing and
//  this dialog said no history could be matched.
//
//  That was true of the place it looked and false about the patient. Measured
//  across this database: 10,080 members hold 12,507 claims, and 1,648 of them
//  (16.3%) have more than one — one member has TEN outpatient visits in four
//  weeks for the same stroke. The attendances were there the whole time, in the
//  claims, and the officer was being told they did not exist.
//
//  It now reads /claims/store/history, which reads BOTH: this hospital's own
//  visit register when the claim carries a hospital patient id, and the member's
//  other claims when it carries a scheme folder number. Rows say which they are,
//  because they are different things — a visit this hospital recorded, versus a
//  visit this hospital billed for.
//
//  ── AND WHAT IS STILL OUTSTANDING ON EACH ──────────────────────────────────
//
//  Notes, lab results and vitals entered from the claims panel are filed under
//  the same folder number, so they attach to these attendances. Each row shows
//  which it has, which turns a list of dates into an answer to the question an
//  officer actually has: which of these visits still needs work.
//
//  ── THE ACTION BUTTON ──────────────────────────────────────────────────────
//
//  For a REGISTERED visit it opens the consulting room's own VisitRecordDialog —
//  that screen's component, not a claims-side retelling of it. For a
//  claim-sourced attendance there is no such record to open, so the row expands
//  in place to show what that claim held. Offering a button that opens an empty
//  window would be worse than not offering one.
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import {
  X, History, FileText, Loader, ChevronDown, ChevronRight, Check,
} from 'lucide-react';
import { claimStoreHistory } from '../BaseURL';
import VisitRecordDialog from '../Doctors/VisitRecordDialog';
import { BRAND } from '../../brand';
import useDialogGuard from './dialogGuard';

const ymd = (v) => String(v || '').slice(0, 10);

const nightsOf = (a) => {
  const d = ymd(a.date); const e = ymd(a.end_date);
  if (!d || !e || e <= d) return 0;
  return Math.round((Date.parse(e) - Date.parse(d)) / 86400000);
};

const TONES = {
  plain: { bg: '#F1F5F9', fg: BRAND.inkMuted, bd: BRAND.line },
  green: { bg: '#E8F5EC', fg: '#1E6B39', bd: 'rgba(30,107,57,.22)' },
  navy: { bg: '#E8EFFA', fg: '#123A6D', bd: 'rgba(18,58,109,.22)' },
  amber: { bg: '#FFF4E5', fg: '#7A4A00', bd: 'rgba(154,91,0,.28)' },
};

function Chip({ tone = 'plain', children, title }) {
  const t = TONES[tone] || TONES.plain;
  return (
    <span title={title} style={{
      display: 'inline-flex', alignItems: 'center', gap: 3,
      padding: '1px 7px', borderRadius: 999,
      fontSize: 10, fontWeight: 800, textTransform: 'uppercase', letterSpacing: .3,
      background: t.bg, color: t.fg, border: `1px solid ${t.bd}`, whiteSpace: 'nowrap',
    }}>{children}</span>
  );
}

export default function PastHistoryDialog({
  open, onClose, patientId, patientName, currentVisit, claimId, claimSource,
}) {
  const [state, setState] = useState({ loading: true, error: null, data: null });
  const [chosen, setChosen] = useState(null);   // a registered visit to open
  const [expanded, setExpanded] = useState(null);
  //  A viewer, so nothing is lost — but it closes by its own Close button like
  //  every other dialog in here rather than being the one that behaves differently.
  const { panelRef, backdropProps } = useDialogGuard();

  const load = useCallback(async () => {
    if (!claimId) {
      setState({ loading: false, data: null,
        error: 'This claim could not be identified.' });
      return;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    try {
      const { data } = await axios.get(claimStoreHistory, {
        params: { id: claimId, source: claimSource },
      });
      setState({ loading: false, error: null, data });
    } catch (e) {
      setState({ loading: false, data: null,
        error: e?.response?.data?.message || e.message });
    }
  }, [claimId, claimSource]);

  useEffect(() => {
    if (!open) return;
    setChosen(null); setExpanded(null);
    load();
  }, [open, load]);

  if (!open) return null;

  const attendances = state.data?.attendances || [];
  const sources = state.data?.sources || { registered_visits: 0, claims: 0 };

  return (
    <div role="dialog" aria-modal="true" aria-label="Past history"
      {...backdropProps}
      style={{
        position: 'fixed', inset: 0, zIndex: 90, background: 'rgba(15,23,42,.42)',
        //  margin:auto on the card, never alignItems:center — a flex container
        //  cannot scroll to content it has centred out of view, so a long list
        //  would push its own header off the top of the screen.
        display: 'flex', padding: '24px 16px', overflow: 'auto',
      }}>
      <div ref={panelRef}
        style={{
          width: '100%', maxWidth: 880, background: '#fff', borderRadius: 12,
          border: `1px solid ${BRAND.line}`, boxShadow: '0 24px 60px rgba(15,23,42,.28)',
          margin: 'auto', maxHeight: 'calc(100vh - 48px)',
          display: 'flex', flexDirection: 'column',
        }}>

        <div style={{
          display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px',
          background: 'linear-gradient(135deg,#123A6D,#0B2452)', color: '#fff',
          borderRadius: '12px 12px 0 0',
        }}>
          <div style={{
            width: 34, height: 34, borderRadius: 9, flexShrink: 0,
            display: 'grid', placeItems: 'center', background: 'rgba(255,255,255,.14)',
          }}><History size={16} /></div>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 15, fontWeight: 800, lineHeight: 1.25 }}>Past history</div>
            <div style={{ fontSize: 11.5, opacity: .82, overflow: 'hidden',
              textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {patientName ? `${patientName} · ` : ''}{patientId || 'no folder number'}
              {attendances.length
                ? ` · ${attendances.length} attendance${attendances.length === 1 ? '' : 's'}`
                : ''}
            </div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close"
            style={{
              border: 0, background: 'rgba(255,255,255,.14)', cursor: 'pointer',
              width: 28, height: 28, borderRadius: 7, color: '#fff',
              display: 'grid', placeItems: 'center', flexShrink: 0,
            }}><X size={14} /></button>
        </div>

        <div style={{ padding: 14, overflow: 'auto', flex: 1 }}>
          {state.loading && (
            <div style={{ padding: '26px 0', textAlign: 'center', color: BRAND.inkMuted,
              fontSize: 12.5, display: 'flex', alignItems: 'center',
              justifyContent: 'center', gap: 8 }}>
              <Loader size={14} /> Looking for this patient's other attendances…
            </div>
          )}

          {state.error && (
            <div style={{
              padding: '10px 12px', borderRadius: 8, fontSize: 12,
              background: BRAND.dangerSoft, border: '1px solid rgba(166,40,40,.22)',
              color: '#A62828',
            }}>{state.error}</div>
          )}

          {/*  Genuinely nothing. Stated as a fact about the RECORD rather than
               about the patient — both sources were read and both were empty,
               which is a different statement from "the id could not be matched",
               and the old wording made that claim on every imported member. */}
          {!state.loading && !state.error && attendances.length === 0 && (
            <div style={{ padding: '22px 24px', color: BRAND.inkSoft, fontSize: 12.5,
              lineHeight: 1.6, textAlign: 'center' }}>
              <strong style={{ color: BRAND.ink }}>No other attendances recorded.</strong>
              <div style={{ marginTop: 6 }}>
                Neither this hospital's visit register nor the claims hold another
                visit for <code>{patientId}</code>.
              </div>
            </div>
          )}

          {attendances.length > 0 && (
            <>
              {/*  Where the list came from. Worth saying: a history built from
                   claims is a record of what was BILLED, and an officer checking
                   whether a patient was really seen should know which they have. */}
              <div style={{ fontSize: 11, color: BRAND.inkMuted, marginBottom: 10,
                lineHeight: 1.5 }}>
                {sources.registered_visits > 0 && (
                  <>{sources.registered_visits} from this hospital's visit register</>
                )}
                {sources.registered_visits > 0 && sources.claims > 0 && ' · '}
                {sources.claims > 0 && (
                  <>{sources.claims} from the member&apos;s claims
                    {sources.registered_visits === 0
                      && ' — this member was not registered here, so the claims are the record of attendance'}</>
                )}
              </div>

              <div style={{ display: 'grid', gap: 6 }}>
                {attendances.map((a, i) => {
                  const nights = nightsOf(a);
                  const isOpen = expanded === i;
                  const doc = a.documented || {};
                  const current = a.is_current
                    || (currentVisit && ymd(currentVisit) === ymd(a.date));
                  return (
                    <div key={`${a.date}-${a.claim_id || 'v'}-${i}`} style={{
                      border: `1px solid ${current ? 'rgba(18,58,109,.35)' : BRAND.line}`,
                      borderRadius: 9, background: current ? '#F7FAFF' : '#fff',
                    }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 10,
                        padding: '9px 11px', flexWrap: 'wrap' }}>
                        <div style={{ minWidth: 92 }}>
                          <div style={{ fontSize: 12.5, fontWeight: 800, color: BRAND.ink }}>
                            {a.date || '—'}
                          </div>
                          {nights > 0 && (
                            <div style={{ fontSize: 10.5, color: BRAND.inkMuted }}>
                              {nights} night{nights === 1 ? '' : 's'}
                            </div>
                          )}
                        </div>

                        <Chip tone={a.attendance === 'inpatient' ? 'navy' : 'plain'}>
                          {a.attendance === 'inpatient' ? 'IPD' : 'OPD'}
                        </Chip>

                        <div style={{ flex: 1, minWidth: 150 }}>
                          <div style={{ fontSize: 12, color: BRAND.ink, overflow: 'hidden',
                            textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {a.diagnosis || a.specialty || '—'}
                          </div>
                          {a.counts && (
                            <div style={{ fontSize: 10.5, color: BRAND.inkMuted, marginTop: 1 }}>
                              {a.counts.diagnoses} dx · {a.counts.procedures} proc
                              {' · '}{a.counts.investigations} inv
                              {' · '}{a.counts.medications} med
                            </div>
                          )}
                        </div>

                        {/*  What documentation this visit already has — the same
                             three the Documentation dialog writes. */}
                        <span style={{ display: 'inline-flex', gap: 3 }}>
                          {[['notes', 'N', 'Notes written'],
                            ['labs', 'L', 'Lab results entered'],
                            ['vitals', 'V', 'Observations taken']].map(([k, label, title]) => (
                              <Chip key={k} tone={doc[k] ? 'green' : 'plain'}
                                title={`${title}: ${doc[k] ? 'yes' : 'no'}`}>{label}</Chip>
                            ))}
                        </span>

                        {current && <Chip tone="navy">this claim</Chip>}

                        {/*  A registered visit has a record to open. A claim does
                             not, so it expands in place rather than offering a
                             button that would open an empty window. */}
                        {a.kind === 'visit' ? (
                          <button type="button"
                            onClick={() => setChosen({ dat: a.date, d_dat: a.end_date })}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 5,
                              padding: '5px 10px', borderRadius: 7, cursor: 'pointer',
                              fontSize: 11.5, fontWeight: 700, font: 'inherit',
                              border: `1px solid ${BRAND.line}`, background: '#fff',
                              color: BRAND.inkSoft,
                            }}><FileText size={12} /> Open record</button>
                        ) : (
                          <button type="button"
                            onClick={() => setExpanded(isOpen ? null : i)}
                            style={{
                              display: 'inline-flex', alignItems: 'center', gap: 4,
                              padding: '5px 9px', borderRadius: 7, cursor: 'pointer',
                              fontSize: 11.5, fontWeight: 700, font: 'inherit',
                              border: `1px solid ${BRAND.line}`, background: '#fff',
                              color: BRAND.inkSoft,
                            }}>
                            {isOpen ? <ChevronDown size={12} /> : <ChevronRight size={12} />}
                            Details
                          </button>
                        )}
                      </div>

                      {isOpen && (
                        <div style={{
                          borderTop: `1px solid ${BRAND.line}`, padding: '9px 11px',
                          fontSize: 11.5, color: BRAND.inkSoft, lineHeight: 1.6,
                          background: '#F8FAFC', borderRadius: '0 0 9px 9px',
                        }}>
                          <div>
                            <strong style={{ color: BRAND.ink }}>Claim</strong> #{a.claim_id}
                            {a.status ? ` · ${a.status}` : ''}
                            {a.outcome ? ` · ${a.outcome}` : ''}
                          </div>
                          {a.specialty && <div>Specialty: {a.specialty}</div>}
                          {a.diagnosis && <div>Principal diagnosis: {a.diagnosis}</div>}
                          {a.end_date && a.end_date !== a.date && (
                            <div>Discharged: {a.end_date}</div>
                          )}
                          <div style={{ marginTop: 5, display: 'flex', gap: 6,
                            flexWrap: 'wrap', alignItems: 'center' }}>
                            {doc.notes && <Chip tone="green"><Check size={9} /> notes</Chip>}
                            {doc.labs && <Chip tone="green"><Check size={9} /> lab results</Chip>}
                            {doc.vitals && <Chip tone="green"><Check size={9} /> vitals</Chip>}
                            {!doc.notes && !doc.labs && !doc.vitals && (
                              <Chip tone="amber">nothing documented for this visit</Chip>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        <div style={{
          padding: '10px 16px', borderTop: `1px solid ${BRAND.line}`,
          background: '#F8FAFC', borderRadius: '0 0 12px 12px',
          display: 'flex', alignItems: 'center', gap: 10,
        }}>
          <span style={{ fontSize: 11.5, color: BRAND.inkMuted, flex: 1 }}>
            Read-only. Nothing here changes the claim.
          </span>
          <button type="button" onClick={onClose}
            style={{
              padding: '7px 14px', borderRadius: 7, cursor: 'pointer',
              fontSize: 12.5, fontWeight: 700, font: 'inherit',
              border: `1px solid ${BRAND.line}`, background: '#fff', color: BRAND.inkSoft,
            }}>Close</button>
        </div>
      </div>

      {/*  The consulting room's own visit record, not a claims-side retelling of
           it. Only ever opened for a REGISTERED visit, because that is the only
           kind it can find anything for. */}
      <VisitRecordDialog
        open={!!chosen}
        onClose={() => setChosen(null)}
        patientData={{ id: patientId, name: patientName }}
        visitRow={chosen} />
    </div>
  );
}
